import { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { onMaximize, onChatEstablished, onSessionContinuation, onChatReady, onBackButtonClick } from './src-utils-chat-chat-event-handler.js';
import { CAContext } from './src-contexts-CAContext.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { eventBus } from './src-utils-event-bus.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import { CaseDataContext } from './src-contexts-CaseDataContext.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { useMIAWApi } from './src-hooks-miaw-useMIAWApi.js';
import { isOrderHelpPage, isStudioPage } from './src-utils-chat-page-utils.js';
import { getConversationId } from './src-utils-miaw-chat-config.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';

const useCustomChatEvents = () => {
  const {
    state: { caseData }
  } = useContext(CaseDataContext);
  const { setState: setMiawState } = useContext(MiawChatContext);
  const { sendTrackingEvent } = useCustomTracking();
  const { logChat } = useNRLogging();
  const { syncCustomerUrlToChat, updateOrderHelpCaseDescription } = useMIAWApi();
  const {
    chatState: { gcpChatData },
    setChatState
  } = useContext(CAContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);
  const [orderHelpPageAttributes, setOrderHelpPageAttributes] = useState({
    description: void 0,
    orderNumber: void 0
  });
  const orderHelpPageAttributesRef = useRef(orderHelpPageAttributes);
  useEffect(() => {
    orderHelpPageAttributesRef.current = orderHelpPageAttributes;
  }, [orderHelpPageAttributes]);
  const initiateOHChat = async (data) => {
    setOrderHelpPageAttributes({
      description: data?.description,
      orderNumber: data?.orderNumber
    });
    if (data?.orderNumber) {
      setChatbotState((prev) => ({
        ...prev,
        orderNumber: data.orderNumber
      }));
    }
    eventBus.dispatch("onCAButtonClicked", data);
  };
  const ohOpenChatHandler = (data) => {
    initiateOHChat(data);
  };
  const [studioDesignLink, setStudioDesignLink] = useState("");
  const populateLiveDesignLink = (conversationId, invokeEvent) => {
    if (typeof window === "undefined") return;
    window?.addEventListener("getLiveDesignURLLink", (e) => {
      if (e?.detail) {
        const designLink = `https://${e.detail}`;
        setStudioDesignLink(designLink);
        const currentPath = window?.location?.origin + window?.location?.pathname;
        if (conversationId) syncCustomerUrlToChat(conversationId, designLink, currentPath);
      }
    });
    if (invokeEvent) {
      window?.dispatchEvent(new CustomEvent("generateLiveDesignLinkEvent"));
    }
  };
  const onChatMessageHandler = (event) => {
    if (caseData) {
      sendTrackingEvent("Chat Initiated", "Chat submitted - Chat Anywhere", `${caseData?.pagePath};Chat Submit`, "Global Chat");
    }
  };
  const onChatEndHandler = () => {
    if (caseData) {
      sendTrackingEvent("Chat Initiated", "Chat closed - Chat Anywhere", `${caseData?.pagePath};Chat Close`, "Global Chat");
    }
    logChat("CA_CHAT_ENDED_BY_USER", {});
  };
  const onChatStartHandler = () => {
    if (caseData) {
      sendTrackingEvent("Chat Initiated", "Chat opened - Chat Anywhere", `${caseData?.pagePath};Chat Start`, "Global Chat");
    }
    setTimeout(() => {
      const conversationId = getConversationId();
      if (conversationId) {
        if (isOrderHelpPage()) updateOrderHelpCaseDescription(conversationId, orderHelpPageAttributesRef.current.description || "");
        if (isStudioPage()) {
          populateLiveDesignLink(conversationId, true);
        }
      }
    }, 5e3);
    onChatEstablished();
    logChat("CA_CHAT_ESTABLISHED", {});
  };
  useEffect(() => {
    if (studioDesignLink) {
      setMiawState((prev) => ({
        ...prev,
        studioDesignLink
      }));
    }
  }, [studioDesignLink]);
  const handleAddEventListeners = useCallback(() => {
    const onMaximizeHandler = () => {
      onMaximize();
    };
    const onMinimizeHandler = () => {
    };
    const onSessionContinuationHandler = () => {
      onSessionContinuation();
      const currentUrl = window.location.href;
      const currentPath = window.location.origin + window.location.pathname;
      const previousUrl = document.referrer;
      const conversationId = getConversationId();
      if (previousUrl !== currentUrl) {
        if (conversationId) {
          syncCustomerUrlToChat(conversationId, currentUrl, currentPath);
          if (isStudioPage()) {
            populateLiveDesignLink(conversationId, true);
          }
        }
      }
    };
    const onChatReadyHandler = () => {
      populateLiveDesignLink(void 0, false);
      onChatReady(gcpChatData?.status?.isChatEnabled || false);
    };
    const onBackButtonClickedHandler = () => {
      onBackButtonClick();
    };
    document.addEventListener("onMaximize", onMaximizeHandler);
    document.addEventListener("onMinimize", onMinimizeHandler);
    document.addEventListener("onChatEnd", onChatEndHandler);
    document.addEventListener("onChatStart", onChatStartHandler);
    document.addEventListener("onSessionContinuation", onSessionContinuationHandler);
    document.addEventListener("onChatMessage", onChatMessageHandler);
    document.addEventListener("onChatReady", onChatReadyHandler);
    document.addEventListener("onBackButtonClicked", onBackButtonClickedHandler);
    setChatState((prevState) => ({
      ...prevState,
      isChatEventListenersAdded: true
    }));
    return () => {
      document.removeEventListener("onMaximize", onMaximizeHandler);
      document.removeEventListener("onMinimize", onMinimizeHandler);
      document.removeEventListener("onChatEnd", onChatEndHandler);
      document.removeEventListener("onChatStart", onChatStartHandler);
      document.removeEventListener("onSessionContinuation", onSessionContinuationHandler);
      document.removeEventListener("onChatMessage", onChatMessageHandler);
      document.removeEventListener("onChatReady", onChatReadyHandler);
      document.removeEventListener("onBackButtonClicked", onBackButtonClickedHandler);
    };
  }, [gcpChatData, caseData, studioDesignLink]);
  const addOpenChatEventListener = useCallback(
    (callback) => {
      const hpOpenChatHandler = () => {
        setChatState((prevState) => ({
          ...prevState,
          chatWindow: "maximized"
        }));
        window?.dispatchEvent(new CustomEvent("generateLiveDesignLinkEvent"));
        callback();
      };
      const onCAButtonClickedHandler = () => {
        callback();
      };
      const initCAfromExternalSourceHandler = () => {
        callback();
      };
      eventBus.on("HPOpenChat", hpOpenChatHandler);
      eventBus.on("onCAButtonClicked", onCAButtonClickedHandler);
      eventBus.on("initCAfromExternalSource", initCAfromExternalSourceHandler);
      eventBus.on("OHOpenChat", ohOpenChatHandler);
      return () => {
        eventBus.remove("HPOpenChat", hpOpenChatHandler);
        eventBus.remove("onCAButtonClicked", onCAButtonClickedHandler);
        eventBus.remove("initCAfromExternalSource", initCAfromExternalSourceHandler);
        eventBus.remove("OHOpenChat", ohOpenChatHandler);
      };
    },
    [initiateOHChat]
  );
  const sendChatEvents = useCallback(
    (eventName, data) => {
      const event = new CustomEvent(eventName, { detail: data });
      document.dispatchEvent(event);
    },
    []
  );
  return { handleAddEventListeners, sendChatEvents, addOpenChatEventListener };
};

export { useCustomChatEvents };
//# sourceMappingURL=src-hooks-custom-hooks-useCustomChatEvents.js.map
